import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginatedResult, PaginationParams } from '@core/common/models/pagination.models';
import {
  ModifierGroupDefinitionMutationResult,
  ProductModifierGroupDefinition,
} from '@features/catalog/models/product-modifier-definition.models';
import { ProductModifierDraftSelectionType } from '@features/catalog/models/product-form-draft.models';
import { ModifierGroupDefinitionApiService } from '@features/catalog/data-access/modifier-group-definition-api.service';

@Injectable()
export class CatalogModifierGroupDefinitionService {
  constructor(private readonly definitionApi: ModifierGroupDefinitionApiService) {}

  list(
    filters?: { activeOnly?: boolean | null; name?: string | null },
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductModifierGroupDefinition>> {
    return this.definitionApi.list(filters, pagination);
  }

  searchByName(name: string): Observable<PaginatedResult<ProductModifierGroupDefinition>> {
    const trimmed = name.trim();
    return this.definitionApi.list({ activeOnly: true, name: trimmed || null });
  }

  getById(id: string): Observable<ProductModifierGroupDefinition | null> {
    return this.definitionApi.getById(id);
  }

  create(input: {
    name: string;
    slug?: string | null;
    description?: string | null;
    selectionType?: ProductModifierDraftSelectionType;
    required?: boolean;
    minSelections?: number;
    maxSelections?: number | null;
    position?: number | null;
    active?: boolean;
  }): Observable<ModifierGroupDefinitionMutationResult> {
    return this.definitionApi.create(input);
  }

  update(input: {
    id: string;
    name?: string;
    slug?: string | null;
    description?: string | null;
    selectionType?: ProductModifierDraftSelectionType;
    required?: boolean;
    minSelections?: number;
    maxSelections?: number | null;
    position?: number | null;
    active?: boolean;
  }): Observable<ModifierGroupDefinitionMutationResult> {
    return this.definitionApi.update(input);
  }

  destroy(id: string): Observable<ModifierGroupDefinitionMutationResult> {
    return this.definitionApi.destroy(id);
  }

  deactivate(id: string): Observable<ModifierGroupDefinitionMutationResult> {
    return this.definitionApi.destroy(id);
  }

  reactivate(id: string): Observable<ModifierGroupDefinitionMutationResult> {
    return this.definitionApi.updateActive(id, true);
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.definitionApi.permanentlyDelete(id);
  }
}
